import { useEffect, useState } from 'react'
import { WifiOff, RefreshCw, CheckCircle } from 'lucide-react'
import { getQueue, dequeue, queueLength } from '../services/offlineQueue'
import api from '../services/api'

export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [pending, setPending] = useState(queueLength())
  const [syncing, setSyncing] = useState(false)
  const [syncedCount, setSyncedCount] = useState(0)

  useEffect(() => {
    const onOnline  = () => setIsOnline(true)
    const onOffline = () => setIsOnline(false)
    const onQueueChange = () => setPending(queueLength())

    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('offline-queue-changed', onQueueChange)

    return () => {
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('offline-queue-changed', onQueueChange)
    }
  }, [])

  // Auto-sync when back online
  useEffect(() => {
    if (isOnline && pending > 0) sync()
  }, [isOnline])

  const sync = async () => {
    if (syncing) return
    setSyncing(true)
    const queue = getQueue()
    let count = 0
    for (const req of queue) {
      try {
        const response = await api.request({ method: req.method as any, url: req.url, data: req.data })
        if (!response?.data?._queued) {
          dequeue(req.id)
          count++
        }
      } catch {
        // Leave failed items in queue for next sync
      }
    }
    setSyncedCount(count)
    setSyncing(false)
    if (count > 0) setTimeout(() => setSyncedCount(0), 3000)
  }

  if (syncedCount > 0) return (
    <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium" style={{ background: '#D1FAE5', color: '#065F46' }}>
      <CheckCircle size={15} /> {syncedCount} item{syncedCount > 1 ? 's' : ''} synced
    </div>
  )

  if (!isOnline) return (
    <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium" style={{ background: '#FEF3C7', color: '#92400E' }}>
      <WifiOff size={15} />
      <span>No internet — actions will sync when reconnected{pending > 0 ? ` (${pending} queued)` : ''}</span>
    </div>
  )

  if (pending > 0) return (
    <div className="flex items-center justify-between px-4 py-2 text-sm font-medium" style={{ background: '#EFF6FF', color: '#1E40AF' }}>
      <span className="flex items-center gap-2">
        <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
        {pending} item{pending > 1 ? 's' : ''} waiting to sync
      </span>
      <button onClick={sync} disabled={syncing} className="font-bold underline">{syncing ? 'Syncing…' : 'Sync now'}</button>
    </div>
  )

  return null
}
